"use client";

import RevealOnScroll from "@/components/atoms/RevealOnScroll";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { hexToRgba } from "@/utils/utils";
import Image from "next/image";
import { useMemo, useRef } from "react";

interface MetaItem1 {
  id?: string | number;
  meta_title?: string;
  meta_title_color?: string;
  meta_description?: string;
  meta_description_color?: string;
  meta_priority?: number;
}

interface MetaItem2 {
  id?: string | number;
  meta_title?: string;
  meta_title_color?: string;
  meta_description?: string;
  meta_description_color?: string;
  meta_priority?: number;
  meta_box_start_color?: string;
  meta_box_end_color?: string;
  meta_box_gradient_direction?: string;
  meta_box_text_color?: string;
}

export interface ApplicationProcess1Data {
  layout_variant?: "variant_1" | "variant_2";
  desktop_background_color?: string;
  mobile_background_color?: string;
  backvariant?: "variant_1" | "variant_2" | "variant_3";
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_color_4?: string;
  pre_title?: string;
  pre_title_color?: string;
  title?: string;
  title_start_color?: string;
  title_end_color?: string;
  title_gradient_direction?: string;
  subtitle_1?: string;
  subtitle_1_color?: string;
  subtitle_1_background_color?: string;
  subtitle_2?: string;
  subtitle_2_color?: string;
  subtitle_2_background_color?: string;
  background_image?: string;
  alt_background_image?: string;
  box_start_color?: string;
  box_end_color?: string;
  gradient_direction?: string;
  enable_animation?: boolean | number;
  status?: boolean;
  metadata_1?: MetaItem1[];
  metadata_2?: MetaItem2[];
}

interface ApplicationProcess1Props {
  data: {
    data: ApplicationProcess1Data;
  };
  className?: string;
}

function ApplicationProcess1({ data, className }: ApplicationProcess1Props) {
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(1080);
  const sectionData = (data?.data ?? data ?? {}) as ApplicationProcess1Data;

  const {
    layout_variant = "variant_1",
    pre_title = "",
    pre_title_color = "#222222CC",
    title = "",
    title_start_color = "#342B7C",
    title_end_color = "#02A7B6",
    title_gradient_direction = "90deg",
    subtitle_1 = "Eligibility Criteria",
    subtitle_1_color = "#FFFFFF",
    subtitle_1_background_color = "#56E2F54D",
    subtitle_2 = "Application Process",
    subtitle_2_color = "#000000",
    subtitle_2_background_color = "#56E2F54D",
    background_image,
    alt_background_image = "",
    box_start_color = "#342B7C",
    box_end_color = "#02A7B6",
    gradient_direction = "180deg",
    enable_animation,
    metadata_1 = [],
    metadata_2 = [],
  } = sectionData;

  const isAnimationEnabled =
    enable_animation === true || enable_animation === 1;

  // Layout variant 2 (Figma): Poppins SemiBold, centered title.
  // Only the title typography/alignment differs from variant 1; the rest of the layout is shared.
  const isV2 = layout_variant === "variant_2";

  // Both layout variants colour the title from the same CMS fields. Variant 2 once
  // hardcoded #101112, so the admin's title colour pickers silently did nothing there.
  // For a solid colour, set start and end to the same value.
  const titleGradientStyle = {
    backgroundImage: `linear-gradient(${title_gradient_direction}, ${title_start_color}, ${title_end_color})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const sortedMetadata1 = useMemo(() => {
    if (!metadata_1?.length) return [];
    return [...metadata_1].sort(
      (a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
    );
  }, [metadata_1]);

  const sortedMetadata2 = useMemo(() => {
    if (!metadata_2?.length) return [];
    return [...metadata_2].sort(
      (a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
    );
  }, [metadata_2]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "application_process_section");
  //   }
  // }, []);

  const bgStyle = formatBackgroundStyle(
    sectionData,
    isMobile ?? false,
    "#FFFFFF"
  );

  const hasContent =
    !!sectionData?.status ||
    sectionData?.title ||
    (sectionData?.metadata_1?.length ?? 0) > 0 ||
    (sectionData?.metadata_2?.length ?? 0) > 0;
  if (!hasContent) return null;

  return (
    <section
      ref={ref}
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        "xs:px-[24px] xs:py-[40px]",
        "sm:px-[60px] sm:pt-[40px] sm:pb-0",
        "md:pt-[60px]",
        "lg:px-[80px] lg:pt-[40px]",
        "xl:px-[140px] xl:pt-[80px]",
        className
      )}
      style={bgStyle}
    >
      <style>{`
        @media (min-width: 1430px) {
          .application-process-pretitle.dark-title-wf { font-size: 30px; }
        }
        @media (min-width: 1750px) {
          .application-process-pretitle.dark-title-wf { font-size: 30px; }
        }
      `}</style>
      <div className={cn("relative w-full", "sm:w-[1080px]", "md:w-[1110px]", "lg:w-[1270px]", "xl:w-[1471px]")}>
        {/* Header: pre-title + gradient title */}
        <div
          className={cn(
            "relative z-20 flex flex-col",
            "xs:gap-[4px] xs:mb-[24px]",
            "sm:mb-[28px]",
            "md:gap-[3px]",
            isV2
              ? "items-center text-center w-full"
              : "sm:max-w-[660px] md:max-w-[751px] lg:max-w-[1100px] xl:max-w-[1200px]"
          )}
        >
          {pre_title && (
            <p
              className="dark-title-wf application-process-pretitle line-clamp-1"
              style={{
                color: pre_title_color,
                fontFamily: "var(--font-google-sans)",
              }}
            >
              {pre_title}
            </p>
          )}
          {title &&
            (isV2 ? (
              // Variant 2 — Figma Poppins SemiBold, centered title.
              // Sits on one line on desktop and wraps to a 2nd line if longer
              // (clamped to 2 lines on desktop, 3 lines on mobile).
              // Not w-full: the gradient is painted across the element's box, so a
              // full-width box behind short centred text shows only the gradient's middle.
              <p
                className={cn(
                  "max-w-full break-words font-semibold",
                  "leading-[1.25] tracking-[-0.015em]",
                  "text-[28px] sm:text-[30px] md:text-[32px] lg:text-[36px]",
                  "line-clamp-3 sm:line-clamp-2"
                )}
                style={{
                  ...titleGradientStyle,
                  fontFamily: "var(--font-poppins)",
                }}
              >
                {title}
              </p>
            ) : (
              <p
                className={cn(
                  "gradient-title-denton-blue",
                  "xs:line-clamp-3 sm:line-clamp-none",
                  "lg:line-clamp-none"
                )}
                style={{
                  ...titleGradientStyle,
                  fontFamily: "var(--font-merriweather)",
                }}
              >
                {title}
              </p>
            ))}
        </div>

        {/* Two-column layout with background image */}
        <div className={cn("relative flex xs:flex-col-reverse xs:gap-[24px]", "sm:flex-row")}>
          {/* Left column: Eligibility Criteria gradient card */}
          <RevealOnScroll y={0} x={-200} enableAnimation={isAnimationEnabled}>

          <div
            className={cn(
              "relative z-10 flex flex-col",
              "xs:rounded-[20px] xs:p-[16px] xs:gap-[32px] xs:mb-0",
              "sm:rounded-[44px] sm:p-[30px] sm:gap-[30px] sm:w-[460px] sm:shrink-0 sm:mb-[20px]",
              "md:rounded-[52px] md:p-[34px] md:gap-[36px] md:w-[538px] md:mb-[23px]",
              "lg:rounded-[60px] lg:p-[40px] lg:gap-[40px] lg:w-[590px] lg:mb-[33px]",
              "xl:rounded-[60px] xl:p-[40px] xl:gap-[40px] xl:w-[766px] xl:mb-[40px]"
            )}
            style={{
              backgroundImage: `linear-gradient(135deg, ${box_start_color}, ${box_end_color})`,
            }}
          >
            {subtitle_1 && (
              <span
                className={cn(
                  "xs:hidden sm:inline-flex items-center justify-center w-fit",
                  "rounded-[20px] px-[16px] py-[8px]",
                  "text-[14px] font-semibold leading-[1.4] tracking-[0.14px]",
                  "sm:rounded-[30px] sm:px-[20px] sm:py-[10px]",
                  "sm:text-[18px] sm:tracking-[0.18px]",
                  "md:text-[16px] md:tracking-[0.16px]",
                  "lg:text-[16px] lg:tracking-[0.16px]",
                  "xl:text-[16px] xl:tracking-[0.16px]",
                  "min-[1080px]:h-[45px] min-[1230px]:h-[42px]"
                )}
                style={{
                  color: subtitle_1_color,
                  backgroundColor: subtitle_1_background_color,
                  fontFamily: "var(--font-google-sans)",
                }}
              >
                {subtitle_1}
              </span>
            )}

            <ul className="flex flex-col list-none p-0 m-0 xs:gap-[32px] sm:gap-[24px] md:gap-[36px] lg:gap-[40px] xl:gap-[40px]">
              {sortedMetadata1.map((item, index) => (
                <li
                  key={item.id ?? index}
                  className={cn(
                    "flex flex-col xs:gap-[6px] sm:gap-[5px] md:gap-[6px]",
                    index === 0
                      ? "sm:max-w-[400px] md:max-w-[470px] lg:max-w-[510px] xl:max-w-[686px]"
                      : "sm:max-w-[317px] md:max-w-[370px] lg:max-w-[390px] xl:max-w-[528px]"
                  )}
                >
                  {item.meta_title && (
                    <span
                      className={cn(
                        "font-semibold leading-[1.4]",
                        "xs:text-[16px] xs:tracking-[0.16px]",
                        "sm:text-[14px] sm:tracking-[0.14px]",
                        "md:text-[16px] md:tracking-[0.16px]",
                        "lg:text-[20px] lg:tracking-[0.2px]",
                        "xl:text-[20px] xl:tracking-[0.2px]"
                      )}
                      style={{
                        color: item.meta_title_color ?? "#FFFFFF",
                        fontFamily: "var(--font-google-sans)",
                      }}
                    >
                      {item.meta_title}
                    </span>
                  )}
                  {item.meta_description && (
                    <span
                      className={cn(
                        "font-semibold leading-[1.4]",
                        "xs:text-[14px] xs:tracking-[0.14px]",
                        "sm:text-[13px] sm:tracking-[0.13px]",
                        "md:text-[15px] md:tracking-[0.15px]",
                        "lg:text-[18px] lg:tracking-[0.18px]",
                        "xl:text-[18px] xl:tracking-[0.18px]"
                      )}
                      style={{
                        color: item.meta_description_color ?? "#FFFFFFB2",
                        fontFamily: "var(--font-google-sans)",
                      }}
                    >
                      {item.meta_description}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          </RevealOnScroll>

          {/* Right column: Application Process steps */}
          <RevealOnScroll y={0} x = {200} enableAnimation={isAnimationEnabled} className={cn(
              "relative z-10 flex flex-col min-w-0 ",
              "gap-[16px]",
              "sm:gap-0 sm:ml-auto"
            )} >
          <div
            className={cn(
              "relative z-10 flex flex-col min-w-0 ",
              "gap-[16px]",
              "sm:gap-0 sm:ml-auto"
            )}
          >
            {subtitle_2 && (
              <span
                className={cn(
                  "xs:hidden sm:inline-flex items-center justify-center w-fit",
                  "rounded-[14px] px-[16px] py-[8px] mb-[16px]",
                  "text-[14px] font-semibold leading-[1.4] tracking-[0.14px]",
                  "sm:rounded-[20px] sm:px-[20px] sm:py-[10px] sm:mb-[24px]",
                  "sm:text-[18px] sm:tracking-[0.18px]",
                  "md:rounded-[30px] md:text-[16px] md:tracking-[0.16px]",
                  "lg:text-[20px] lg:tracking-[0.2px]",
                  "xl:text-[20px] xl:tracking-[0.2px]",
                  "min-[1080px]:h-[45px] min-[1230px]:h-[42px] min-[1430px]:h-[48px]"
                )}
                style={{
                  color: subtitle_2_color,
                  backgroundColor: subtitle_2_background_color,
                  fontFamily: "var(--font-google-sans)",
                }}
              >
                {subtitle_2}
              </span>
            )}

            <ul className={cn(
              "flex flex-col list-none p-0 m-0",
              "xs:gap-[50px]",
              "sm:gap-0"
            )}>
              {sortedMetadata2.map((item, index) => {
                const serialNumber = String(index + 1).padStart(2, "0");
                const isLast = index === sortedMetadata2.length - 1;
                const stepBoxGradient = `linear-gradient(${item.meta_box_gradient_direction ?? "180deg"}, ${item.meta_box_start_color ?? box_start_color}, ${item.meta_box_end_color ?? box_end_color})`;
                const stepTextColor = item.meta_box_text_color ?? "#FFFFFF";

                return (
                  <li key={item.id ?? index} className={cn("flex", "xs:items-center", "sm:items-stretch")}>
                    {/* Number column with connecting line */}
                    <div className={cn("flex flex-col items-center shrink-0", "xs:relative")}>
                      <div
                        className={cn(
                          "flex items-center justify-center font-semibold",
                          "xs:w-[60px] xs:h-[60px] xs:text-[22px]",
                          "sm:w-[60px] sm:h-[60px] sm:text-[25px]",
                          "md:w-[69px] md:h-[68px] md:text-[28px]",
                          "lg:w-[80px] lg:h-[80px] lg:text-[32px]",
                          "xl:w-[80px] xl:h-[80px] xl:text-[32px]"
                        )}
                        style={{
                          background: stepBoxGradient,
                          color: stepTextColor,
                          fontFamily: "var(--font-google-sans)",
                        }}
                      >
                        {serialNumber}
                      </div>
                      {!isLast && (
                        <div
                          className={cn(
                            "w-[1.5px]",
                            "xs:absolute xs:top-full xs:left-1/2 xs:-translate-x-1/2 xs:h-[50px]",
                            "sm:relative sm:top-auto sm:left-auto sm:translate-x-0 sm:flex-1",
                            "sm:min-h-[66px]",
                            "md:min-h-[73px]",
                            "lg:min-h-[80px]",
                            "xl:min-h-[80px]"
                          )}
                          style={{ backgroundColor: "#C5C5C5" }}
                        />
                      )}
                    </div>

                    {/* Step content */}
                    <div
                      className={cn(
                        "flex flex-col min-w-0",
                        "xs:ml-[16px] xs:gap-[8px]",
                        "sm:ml-[20px] sm:gap-[5px] sm:pt-[4px]",
                        isLast ? "sm:pb-0" : "sm:pb-[8px]",
                        "sm:w-[280px]",
                        "md:ml-[20px] md:gap-[6px]",
                        "md:w-[320px]",
                        "lg:ml-[27px] lg:gap-[8px]",
                        "lg:w-[372px]",
                        "xl:ml-[27px] xl:gap-[8px]",
                        "xl:w-[372px]"
                      )}
                    >
                      {item.meta_title && (
                        <span
                          className={cn(
                            "font-semibold leading-[1.4]",
                            "xs:text-[16px] xs:tracking-[0.16px]",
                            "sm:text-[14px] sm:tracking-[0.14px]",
                            "md:text-[16px] md:tracking-[0.16px]",
                            "lg:text-[20px] lg:tracking-[0.2px]",
                            "xl:text-[20px] xl:tracking-[0.2px]"
                          )}
                          style={{
                            color: item.meta_title_color ?? "#000000",
                            fontFamily: "var(--font-google-sans)",
                          }}
                        >
                          {item.meta_title}
                        </span>
                      )}
                      {item.meta_description && (
                        <span
                          className={cn(
                            "font-semibold leading-[1.4]",
                            "xs:text-[14px] xs:tracking-[0.14px]",
                            "sm:text-[13px] sm:tracking-[0.13px]",
                            "md:text-[15px] md:tracking-[0.15px]",
                            "lg:text-[18px] lg:tracking-[0.18px]",
                            "xl:text-[18px] xl:tracking-[0.18px]"
                          )}
                          style={{
                            color:
                              hexToRgba(item.meta_description_color || "#000000", 0.7),
                            fontFamily: "var(--font-google-sans)",
                          }}
                        >
                          {item.meta_description}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          </RevealOnScroll>
        </div>

        {/* Background image (person) – overlaps left box, extends to section bottom */}
        {background_image && (
          <div
            className={cn(
              "hidden sm:block pointer-events-none absolute z-[11]",
              "bottom-0 left-[299px]",
              "w-[294px] h-[392px]",
              "md:left-[348px] md:w-[336px] md:h-[449px]",
              "lg:left-[360px] lg:w-[421px] lg:h-[561px]",
              "xl:left-[525px] xl:w-[421px] xl:h-[561px]"
            )}
          >
            <RevealOnScroll
              className="w-full h-full"
              y={400}
              enableAnimation={isAnimationEnabled}
            >
            <Image
              src={background_image}
              alt={alt_background_image}
              fill
              className="object-cover object-bottom"
              sizes="294px"
            />
            </RevealOnScroll>
          </div>
        )}
      </div>
    </section>
  );
}

export default ApplicationProcess1;
